import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { env, storageKeys } from '@/config/env';
import { en } from './locales/en';
import { es } from './locales/es';

export const SUPPORTED_LANGUAGES = ['en', 'es'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export function isLanguage(value: unknown): value is Language {
  return SUPPORTED_LANGUAGES.includes(value as Language);
}

/**
 * The stored choice, or the client's configured default.
 *
 * The old app wrote `JSON.stringify(lang)` into localStorage — so the value came
 * back as `"en"`, quotes included, and every read had to `.replaceAll('"', '')`
 * to recover it. Miss one and the language silently reset. The plain string is
 * stored here, and a legacy quoted value is still tolerated on first read.
 */
export function readStoredLanguage(): Language {
  const stored = localStorage.getItem(storageKeys.language)?.replace(/"/g, '');
  if (isLanguage(stored)) return stored;
  return isLanguage(env.defaultLanguage) ? env.defaultLanguage : 'en';
}

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    es: { translation: es },
  },
  // The old config hard-coded `lng: 'es'` and ignored the configured default
  // entirely, so MECA — an `en` client — opened in Spanish every time.
  lng: readStoredLanguage(),
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LANGUAGES,
  interpolation: { escapeValue: false },
  returnNull: false,
});

// Keep the document in step from the first paint, not only after a switch —
// it drives the browser's own hyphenation, spellcheck and screen-reader voice.
document.documentElement.lang = i18n.resolvedLanguage ?? 'en';
i18n.on('languageChanged', (next) => {
  document.documentElement.lang = next;
});

export default i18n;
